import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { deepLink, siteFolderName } from '@erp-attach/shared';
import { useApiQuery } from '../api/useApiQuery';
import { ApiRequestError } from '../api/client';
import { config } from '../config';
import { addRecent, notifyRecentChange } from '../lib/recent';
import { routes } from '../lib/links';
import { EntityHeader } from '../components/EntityHeader';
import { FolderWorkspace } from '../components/FolderWorkspace';
import { ProvisioningState } from '../components/ProvisioningState';
import { ArchiveJobButton } from '../components/ArchiveJobButton';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { FullPageSpinner } from '../components/ui/Spinner';
import { NotFoundPage } from './NotFoundPage';

export function CustomerPage() {
  const { accountNumber = '' } = useParams();
  const query = useApiQuery((api, signal) => api.customers.get(accountNumber, signal), [accountNumber]);
  const data = query.data;

  useEffect(() => {
    if (data?.status === 'ready') {
      addRecent({
        kind: 'customer',
        number: data.entity.accountNumber,
        label: data.entity.name,
        href: routes.customer(data.entity.accountNumber),
      });
      notifyRecentChange();
    }
  }, [data]);

  if (query.loading) return <FullPageSpinner label="Loading customer" />;
  if (query.error) {
    if (query.error instanceof ApiRequestError && query.error.isNotFound) return <NotFoundPage what="customer" />;
    return <ErrorBanner title="Customer could not be loaded" error={query.error} />;
  }
  if (!data) return null;
  if (data.status === 'provisioning') {
    return <ProvisioningState kind="customer" number={data.entity.number || accountNumber} name={data.entity.name} onRetry={query.reload} />;
  }

  const { entity, folders } = data;
  return (
    <div className="space-y-4">
      <EntityHeader
        kind="customer"
        crumbs={[{ label: 'Customers', href: routes.browseCustomers }]}
        name={entity.name}
        number={entity.accountNumber}
        branchCode={entity.branchCode}
        status={entity.status}
        deepLink={deepLink.customer(entity.accountNumber, config.appBaseUrl)}
      />
      <FolderWorkspace
        entityType="customer"
        folders={folders}
        folderActions={(folder) =>
          folder.siteId ? (
            <ArchiveJobButton
              siteId={folder.siteId}
              jobReference={siteFolderName(folder.jobReference, folder.siteId)}
              archived={folder.status === 'archived'}
              canArchive={folder.actions.includes('archive')}
              onDone={query.reload}
            />
          ) : null
        }
      />
    </div>
  );
}
